import React, { FunctionComponent, useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next'
import useMediaQuery from '@mui/material/useMediaQuery';

// import { check_subdomain } from '../../lib/server_helper';
import { fetch_one, fetch_all } from "../../../lib/v31lib";
import { loginAtom } from '../../../lib/auth';
import { currentPracticeAtom } from '../../../lib/practice';

import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import PanToolAltIcon from '@mui/icons-material/PanToolAlt';

import {
  Avatar,
  Box,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Step,
  StepLabel,
  Stepper,
} from '@mui/material';

import {
  Button,
  IconButton,
  Page,
  Typography
} from '../../../components/v2/styled';
import { ClientAppointmentNewStepCoach } from './new_steps/Coach';
import { ClientAppointmentNewStepConfirm } from './new_steps/Confirm';
import { ClientAppointmentNewStepLocation } from './new_steps/Location';
import { ClientAppointmentNewStepSlot } from './new_steps/Slot';
import { ClientAppointmentNewStepType } from './new_steps/Type';
import { SnackError } from '../../../components/v2/snacks/Error';

import HelpersAppointmentCombos from '../../../actions/helpers/appointment_combos';
import HelpersLanguage from '../../../actions/helpers/language';
import { AppointmentType } from '../../../models/AppointmentType';
import { Consent } from '../../../models/Consent';
import { Location } from '../../../models/Location';
import { Practice } from '../../../models/Practice';
import { User } from '../../../models/User';

// const { DateTime } = require("luxon");

type Props = {}

export const ClientAppointmentNew: FunctionComponent<Props> = ({}) => {
  const [login, setLogin] = useAtom(loginAtom);
  const [currentPractice, setCurrentPractice] = useAtom(currentPracticeAtom);
  const theme = useTheme();
  const navigate = useNavigate();
  const {t, i18n} = useTranslation(['translations']);
  // const queryClient = useQueryClient();
  // const { state } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const matchesmd = useMediaQuery(theme.breakpoints.up('md'));

  const [activePractice, setActivePractice] = useState<Practice>();
  const [currentType, setCurrentType] = useState<AppointmentType>();
  const [currentCoach, setCurrentCoach] = useState<User>();
  const [noCoach, setNoCoach] = useState<boolean>(false);
  const [currentLocation, setCurrentLocation] = useState<Location>();
  const [showSnackError, setShowSnackError] = useState<boolean>(false);
  const [showSnackBlockedError, setShowSnackBlockedError] = useState<boolean>(false);
  const [showInvalidComboError, setShowInvalidComboError] = useState<boolean>(false);
  const [interacted, setInteracted] = useState<boolean>(false);
  const [noLocation, setNoLocation] = useState<boolean>(false);
  const [selectedSlot, setSelectedSlot] = useState<[string, Location, User]>();
  const [appointment, setAppointment] = useState<{
    note: string,
    reminder_mail: boolean,
    reminder_sms: boolean
  }>({
    note: '',
    reminder_mail: true,
    reminder_sms: false
  });
  const [validatedUser, setValidatedUser] = useState<User>();
  const [validUser, setValidUser] = useState<boolean>(false);
  const [newUser, setNewUser] = useState<User>();

  const {
    data: coaches,
    // isLoading: coachesLoading,
    // isError: coachesError,
    isSuccess: coachesSuccess,
    // refetch: coachesRefetch,
  } = useQuery({
    queryKey: ["coach", activePractice?.id],
    queryFn: () =>
      fetch_all<User>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/pub/fetch_all`,
        {
          object: 'coach',
          fields: [
            'id', 'full_name', 'coach_skills', 'get_medium', 'enabled', 'practice_id'
          ],
          page: 0,
          per_page: 1000,
          order: "first_name ASC, last_name ASC",
          filter: {
            search: '',
            advanced: {
              practice_id: activePractice?.id,
              enabled: 1,
              is_secretary: 0,
              with_roster: 1
            }
          }
        },
        login
      ),
    // select: (d) => {
    //   hasVideo = d.filter(ff => ff.is_meeting).length > 0;
    //   hasPhysical = d.filter(ff => !ff.is_meeting).length > 0;
    //   return d;
    // },
    enabled: !!activePractice?.id && !!searchParams.get("coach_id")
  });

  const {
    data: appointmentTypes,
    // isLoading: appointmentTypesLoading,
    // isError: appointmentTypesError,
    isSuccess: appointmentTypesSuccess,
    // refetch: appointmentTypesRefetch,
  } = useQuery({
    queryKey: ["appointment_types", activePractice?.id],
    queryFn: () =>
      fetch_all<AppointmentType>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/pub/fetch_all`,
        {
          object: 'appointment_type',
          fields: [
            'id', 'name', 'get_name', 'duration', 'duration_nice', 'public_duration', 'public_duration_nice', 'is_meeting', 'colour', 'cost', 'coaches', 'mollie', 'note_required', 'extra_data'
          ],
          page: 0,
          per_page: 1000,
          order: "sort_order ASC, name ASC",
          filter: {
            search: '',
            advanced: {
              practice_id: activePractice?.id,
              enabled: 1,
              visible: 1
            }
          }
        }
      ),
    // select: (d) => {
    //   hasVideo = d.filter(ff => ff.is_meeting).length > 0;
    //   hasPhysical = d.filter(ff => !ff.is_meeting).length > 0;
    //   return d;
    // },
    enabled: !!activePractice?.id,
  });

  const {
    data: locations,
    // isLoading: locationsLoading,
    // isError: locationsError,
    isSuccess: locationsSuccess,
    // refetch: locationsRefetch,
  } = useQuery({
    queryKey: ["locations", activePractice?.id],
    queryFn: () =>
      fetch_all<Location>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/pub/fetch_all`,
        {
          object: 'location',
          fields: [
            'id', 'name', 'get_medium', 'street', 'number', 'zip', 'city', 'is_online', 'is_physical', 'coaches', 'invisible_coaches'
          ],
          page: 0,
          per_page: 1000,
          order: "name ASC",
          filter: {
            search: '',
            advanced: {
              practice_id: activePractice?.id,
              enabled: 1,
              is_hidden: 0
            }
          }
        },
        login
      ),
    // select: (d) => {
    //   hasVideo = d.filter(ff => ff.is_meeting).length > 0;
    //   hasPhysical = d.filter(ff => !ff.is_meeting).length > 0;
    //   return d;
    // },
    enabled: !!activePractice?.id && !!searchParams.get("location_id") && (!!currentCoach || noCoach)
  });

  const {
    data: myPractices,
    // isLoading: objectsLoading,
    // isError: objectsError,
    // isSuccess: objectsSuccess,
    // refetch: objectsRefetch,
  } = useQuery({
    queryKey: ["my_practices_for_new_app", login?.practice_id],
    queryFn: () =>
      fetch_all<Consent>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'consent',
          fields: ['id', 'practice', 'enabled'],
          sub_fields: {
            practice: ['id', 'name', 'get_medium', 'settings', 'get_description', 'enabled']
          },
          filter: {
            search: '',
            advanced: {
              user_id: login?.id
            }
          }
        },
        login
      ),
    enabled: !!login && login.type === "User::User"
  });
  

  // const [currentDay, setCurrentDay] = useState<string>(!!state && !!state.day ? state.day : DateTime.now().toFormat("dd/LL/y"));
  // const [statDay, setStatDay] = useState<string>(!!state && !!state.day ? state.day : DateTime.now().toFormat("dd/LL/y"));
  // let count = 0;
  // const [page, setPage] = useState<number>(0);

  // const {
  //   data: appointmentsSoon,
  //   isLoading: appointmentsSoonLoading,
  //   isError: appointmentsSoonError,
  //   isSuccess: appointmentsSoonSuccess,
  //   refetch: appointmentsSoonRefetch,
  // } = useQuery({
  //   queryKey: ["appointments_soon", login?.id, currentDay],
  //   queryFn: () =>
  //     fetch_all<Appointment>(
  //       `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
  //       {
  //         object: 'appointment',
  //         fields: [
  //           'id', 'user_id', 'start_time', 'end_time', 'is_cancelled', 'is_cancelled_by_coach', 'is_cancelled_late', 'is_noshow', 'location|id/get_item/name/is_online/street_line/city_line', 'coach|id/get_item/coach_skills/full_name', 'practice|id/name', 'cancel_note', 'order|id/paid',
  //           'appointment_type|id/colour/cost/duration_nice/name'
  //         ],
  //         page: 0,
  //         per_page: 20,
  //         filter: {
  //           search: '',
  //           advanced: {
  //             user_id: login?.id,
  //             soon: 1
  //           }
  //         }
  //       },
  //       login
  //     ),
  //   // select: (d) => {
  //   //   return d.custom_result.data;
  //   // },
  //   enabled: !!login,
  // });







  const mutationBookSlot = useMutation({
    mutationFn: (pars: {
      formData: any;
    }) => {
      return fetch_one<{
        custom_result: {
          success: boolean,
          error?: string
        }
      }>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/pub/custom_action`,
        pars.formData,
        login
      );
    },
    onMutate: (pars: {
      formData: any;
    }) => {
      // setIsEditPasswordError(false);
      // setEditPasswordSuccess(false);
      // setEditPasswordError(undefined);
      // setEditPasswordLoading(true);
      return true;
    },
    onError: (data, variables, context) => {
      // setIsEditPasswordError(true);
      // setEditPasswordError(data as any);
      // setEditPasswordLoading(false);
      return true;
    },
    onSuccess: (data, variables, context) => {
      // diaryDayRefetch();
      // lastWeightRefetch();
      // queryClient.invalidateQueries({ queryKey: ["diary_day", login?.id, currentDay] });
      // queryClient.invalidateQueries({ queryKey: ["weight_stats", login?.id, currentDay, statRange] });
      // snack?
      console.log(data);
      if (!!data.custom_result.success) {
        if (!!login) {
          navigate('/appointments', {state: {show_appointment_success: true}});
        } else {
          navigate('/login', {state: {show_appointment_success: true}});
        }
      } else {
        if (data.custom_result.error === 'blocked') {
          setShowSnackBlockedError(true);
        } else {
          setShowSnackError(true);
        }
      }
    },
  });







  function isRichTextEmpty(value: string | undefined | null): boolean {
    if (typeof value !== 'string' || !value) return true;

    const text = new DOMParser()
      .parseFromString(value, 'text/html')
      .documentElement.textContent
      ?.replace(/\u00A0/g, ' ')  // &nbsp; -> regular space
      .trim();

    return !text;
  }







  
  // const [open, setOpen] = useState<number>();
  const [currentStep, setCurrentStep] = useState<number>(0);
  
  // useEffect(() => {
  //   if (!!diaryDay?.weight) setCurrentWeight(diaryDay?.weight);
  // }, [diaryDay]);
  // useEffect(() => {
  //   if (!!lastWeight?.weight) setCurrentWeight(lastWeight?.weight || currentWeight || 0);
  // }, [lastWeight]);

  // let inputWeightDisabled = false;
  // if (!!diaryDaySuccess && !!diaryDay.weight) inputWeightDisabled = true;

  useEffect(() => {
    if (!!appointmentTypesSuccess) {
      let tmpAppType = appointmentTypes.filter(fff => (fff.id || 0) === parseInt((searchParams.get("appointment_type_id") || '-1'), 10))[0];
      if (!!tmpAppType) {
        setCurrentType(tmpAppType);
        if (!interacted) setCurrentStep(1);
      }
    }
    if (!!coachesSuccess) {
      if (parseInt((searchParams.get("coach_id") || '0'), 10) === -1) {
        setNoCoach(true);
        if (currentStep === 1 && !interacted) setCurrentStep(2);
      } else if (!interacted && !!searchParams.get("coach_id")) {
        let tmpCoach = coaches.filter(fff => (fff.id || 0) === parseInt((searchParams.get("coach_id") || '0'), 10))[0];
        if (!!tmpCoach && HelpersAppointmentCombos.coachIsValidForType(tmpCoach, currentType)) {
          setCurrentCoach(tmpCoach);
          if (currentStep === 1 && !interacted) setCurrentStep(2);
        } else {
          setShowInvalidComboError(true);
          setInteracted(true);
          setCurrentCoach(undefined);
          let next = new URLSearchParams(searchParams);
          next.delete("coach_id");
          setSearchParams(next);
        }
      }
    }
    if (!!locationsSuccess) {
      if (parseInt((searchParams.get("location_id") || '0'), 10) === -1) {
        setNoLocation(true);
        if (currentStep === 2 && !interacted) setCurrentStep(3);
      } else if (!interacted && !!searchParams.get("location_id") && (!!currentCoach || noCoach)) {
        let tmpLocation = locations.filter(fff => (fff.id || 0) === parseInt((searchParams.get("location_id") || '0'), 10))[0];
        if (!!tmpLocation && HelpersAppointmentCombos.locationIsValidForCombo(tmpLocation, currentCoach, currentType)) {
          setCurrentLocation(tmpLocation);
          if (currentStep === 2 && !interacted) setCurrentStep(3);
        } else {
          setShowInvalidComboError(true);
          setInteracted(true);
          setCurrentLocation(undefined);
          let next = new URLSearchParams(searchParams);
          next.delete("location_id");
          setSearchParams(next);
        }
      }
    }
    // console.log(`cc: ${JSON.stringify(currentCoach)}`);
  }, [searchParams.get("appointment_type_id"), searchParams.get("coach_id"), searchParams.get("location_id"), locationsSuccess, coachesSuccess, appointmentTypesSuccess, currentType, currentCoach]);
  useEffect(() => {
    // console.log('check', !!login, myPractices, (myPractices || []).filter(fff => fff.enabled).length > 1);
    if (!!login && (myPractices || []).filter(fff => fff.enabled).length > 1) {
      setActivePractice(undefined);
    } else {
      if (!!login && (myPractices || []).filter(fff => fff.enabled).length === 1) {
        setActivePractice((myPractices || []).filter(fff => fff.enabled)[0].practice);
    } else {
      setActivePractice(currentPractice);
    }
    }
  }, [currentPractice, myPractices]);

  return <Page
    title={!!activePractice?.name ? `${t("client.menu.appointment")} ~ ${activePractice?.name}` : t("client.menu.appointment")}
    isLanguagePicker
  >
    {!(!!login || (!login && !!activePractice?.settings?.is_allow_guest_accounts)) && <>
      <Typography sx={{color: theme.palette.secondary.main, fontStyle: 'italic'}}>{t("client.appointment.unavailable")}</Typography>
      <Button
        id="back"
        onClick={() => {
          navigate('/');
        }}
        label={t("button_back", "Terug")}
      />
    </>}
    {(!!login || (!login && !!activePractice?.settings?.is_allow_guest_accounts)) && <>
      <Stepper sx={{
        display: {
          xs: 'none',
          md: 'flex'
        },
        marginTop: 3
      }} activeStep={currentStep} orientation={matchesmd ? "horizontal" : "vertical"}>
        {[
          t("client.appointment.step.type", "Type"),
          t("client.appointment.step.coach", "Coach"),
          t("client.appointment.step.location", "Locatie"),
          t("client.appointment.step.date", "Datum"),
          t("client.appointment.step.confirm", "Bevestiging")
        ].map((label, index) => {
          return (<Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>);
        })}
      </Stepper>
      <Box sx={{
        display: {
          xs: 'block',
          md: 'none'
        },
        paddingLeft: 8,
        position: 'relative'
      }}>
        <ArrowForwardIosIcon sx={{position: 'absolute', top: 0, left: 8, color: theme.palette.primary.main}} />
        <ArrowForwardIosIcon sx={{position: 'absolute', top: 0, left: 16, color: currentStep >= 1 ? theme.palette.primary.main : 'lightgrey'}} />
        <ArrowForwardIosIcon sx={{position: 'absolute', top: 0, left: 24, color: currentStep >= 2 ? theme.palette.primary.main : 'lightgrey'}} />
        <ArrowForwardIosIcon sx={{position: 'absolute', top: 0, left: 32, color: currentStep >= 3 ? theme.palette.primary.main : 'lightgrey'}} />
        <ArrowForwardIosIcon sx={{position: 'absolute', top: 0, left: 40, color: currentStep >= 4 ? theme.palette.primary.main : 'lightgrey'}} />
        {currentStep === 0 && <Typography variant='h3' sx={{}}>{t("client.appointment.step.type", "Type")}</Typography>}
        {currentStep === 1 && <Typography variant='h3' sx={{}}>{t("client.appointment.step.coach", "Coach")}</Typography>}
        {currentStep === 2 && <Typography variant='h3' sx={{}}>{t("client.appointment.step.location", "Locatie")}</Typography>}
        {currentStep === 3 && <Typography variant='h3' sx={{}}>{t("client.appointment.step.date", "Datum")}</Typography>}
        {currentStep === 4 && <Typography variant='h3' sx={{}}>{t("client.appointment.step.confirm", "Bevestiging")}</Typography>}
      </Box>

      {!activePractice && <>
        <Box>
          <Box sx={{
            marginTop: 3,
            marginBottom: 3
          }}>
            <Typography variant="subtitle2">{t("client.new_appointment.multiple_practices.title")}</Typography>
            <List>
              {(myPractices || []).filter(fff => !!fff.enabled && !!fff.practice?.enabled).map(practice => (<>
                <ListItem
                  divider={(myPractices || []).indexOf(practice) < ((myPractices || []).length -1)}
                  sx={{
                    cursor: 'pointer'
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    setActivePractice(practice.practice);
                  }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{
                      backgroundColor: 'white',
                      border: '1px solid transparent',
                      borderColor: theme.palette.primary.main,
                      "& img": {
                        maxHeight: '100%',
                        maxWidth: '100%',
                        width: 'auto',
                        height: 'auto'
                      }
                    }} src={practice.practice?.get_medium} alt={practice.practice?.name}></Avatar>
                  </ListItemAvatar>
                  <ListItemText primary={practice.practice?.name} />
                  <ListItemSecondaryAction>
                    <IconButton onClick={(e) => {
                      e.preventDefault();
                      setActivePractice(practice.practice);
                    }}>
                      <PanToolAltIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              </>))}
            </List>
          </Box>
        </Box>
      </>}
      {!!activePractice && <>
        <Box>
          <Box sx={{
            marginTop: 3,
            marginBottom: 3
          }}>
            {currentStep === 0 && !!activePractice && !isRichTextEmpty((activePractice.settings?.new_appointment_custom_message || {})[HelpersLanguage.mapping_to_id(i18n.resolvedLanguage || 'nl')]) && <Paper sx={{
              padding: 2,
              marginBottom: 2
            }}>
              <Typography html>{(activePractice.settings?.new_appointment_custom_message || {})[HelpersLanguage.mapping_to_id(i18n.resolvedLanguage || 'nl')]}</Typography>
            </Paper>}
            {/* <Pane>{this.getStep(currentStep)}</Pane> */}
            {currentStep === 0 && <ClientAppointmentNewStepType
              activeCoach={currentCoach}
              activeLocation={currentLocation}
              activePractice={activePractice}
              selectedType={currentType}
              setSelectedType={(type) => {
                setCurrentType(type);
                if (!!searchParams.get("coach_id") && !!searchParams.get("location_id")) {
                  // reset step 4
                  setSelectedSlot(undefined);
                  // reset step 5
                  setAppointment({
                    note: '',
                    reminder_mail: true,
                    reminder_sms: false
                  });
                  setValidatedUser(undefined);
                  setValidUser(false);
                  setNewUser(undefined);
                } else {
                  if (!searchParams.get("coach_id")) {
                    // reset step 2
                    setCurrentCoach(undefined);
                    setNoCoach(false);
                  }
                  if (!searchParams.get("location_id")) {
                    // reset step 3
                    setCurrentLocation(undefined);
                    setNoLocation(false);
                  }
                  // reset step 4
                  setSelectedSlot(undefined);
                  // reset step 5
                  setAppointment({
                    note: '',
                    reminder_mail: true,
                    reminder_sms: false
                  });
                  setValidatedUser(undefined);
                  setValidUser(false);
                  setNewUser(undefined);
                }
              }}
            />}
            {currentStep === 1 && !!currentType && <ClientAppointmentNewStepCoach
              noCoach={noCoach}
              selectedType={currentType}
              selectedCoach={currentCoach}
              activePractice={activePractice}
              setNoCoach={(bool) => {
                setCurrentCoach(undefined);
                setNoCoach(bool);
                if (!searchParams.get("location_id")) {
                  // reset step 3
                  setCurrentLocation(undefined);
                  setNoLocation(false);
                }
                // reset step 4
                setSelectedSlot(undefined);
                // reset step 5
                setAppointment({
                  note: '',
                  reminder_mail: true,
                  reminder_sms: false
                });
                setValidatedUser(undefined);
                setValidUser(false);
                setNewUser(undefined);
              }}
              setSelectedCoach={(coach) => {
                setCurrentCoach(coach);
                setNoCoach(false);
                if (!searchParams.get("location_id")) {
                  // reset step 3
                  setCurrentLocation(undefined);
                  setNoLocation(false);
                }
                // reset step 4
                setSelectedSlot(undefined);
                // reset step 5
                setAppointment({
                  note: '',
                  reminder_mail: true,
                  reminder_sms: false
                });
                setValidatedUser(undefined);
                setValidUser(false);
                setNewUser(undefined);
              }}
            />}
            {currentStep === 2 && !!currentType && (!!currentCoach || noCoach) && <ClientAppointmentNewStepLocation
              noCoach={noCoach}
              noLocation={noLocation}
              selectedType={currentType}
              selectedCoach={currentCoach}
              selectedLocation={currentLocation}
              activePractice={activePractice}
              setNoLocation={(bool) => {
                setCurrentLocation(undefined);
                setNoLocation(bool);
                // reset step 4
                setSelectedSlot(undefined);
                // reset step 5
                setAppointment({
                  note: '',
                  reminder_mail: true,
                  reminder_sms: false
                });
                setValidatedUser(undefined);
                setValidUser(false);
                setNewUser(undefined);
              }}
              setSelectedLocation={(coach) => {
                setCurrentLocation(coach);
                setNoLocation(false);
                // reset step 4
                setSelectedSlot(undefined);
                // reset step 5
                setAppointment({
                  note: '',
                  reminder_mail: true,
                  reminder_sms: false
                });
                setValidatedUser(undefined);
                setValidUser(false);
                setNewUser(undefined);
              }}
            />}
            {currentStep === 3 && !!currentType && (!!currentCoach || noCoach) && (!!currentLocation || noLocation) && <ClientAppointmentNewStepSlot
              noCoach={noCoach}
              noLocation={noLocation}
              selectedType={currentType}
              selectedCoach={currentCoach}
              selectedLocation={currentLocation}
              selectedSlot={selectedSlot}
              onNoSlots={() => {
                if (!interacted && (!!searchParams.get("coach_id") || !!searchParams.get("location_id"))) {
                  setShowInvalidComboError(true);
                  setInteracted(true);
                  setCurrentLocation(undefined);
                  setNoLocation(false);
                  let next = new URLSearchParams(searchParams);
                  next.delete("location_id");
                  setSearchParams(next);
                  setCurrentStep(2);
                }
              }}
              activePractice={activePractice}
              setSelectedSlot={(slot) => {
                setSelectedSlot(slot);
                // reset step 5
                setAppointment({
                  note: '',
                  reminder_mail: true,
                  reminder_sms: false
                });
                setValidatedUser(undefined);
                setValidUser(false);
                setNewUser(undefined);
              }}
            />}
            {currentStep === 4 && !!currentType && (!!currentCoach || noCoach) && (!!currentLocation || noLocation) && !!selectedSlot && <ClientAppointmentNewStepConfirm
              noCoach={noCoach}
              noLocation={noLocation}
              selectedType={currentType}
              selectedCoach={currentCoach}
              selectedLocation={currentLocation}
              selectedSlot={selectedSlot}
              appointment={appointment}
              validatedUser={validatedUser}
              validUser={validUser}
              activePractice={activePractice}
              setAppointment={(appointment) => {
                setAppointment(appointment);
              }}
              setValidatedUser={(user) => {
                setValidatedUser(user);
              }}
              setValidUser={(bool) => {
                setValidUser(bool);
              }}
              setNewUser={(user) => {
                setNewUser(user);
              }}
            />}
          </Box>
          <Box>
            <Button
              disabled={currentStep === 0 && !(!!login && (myPractices || []).filter(fff => fff.enabled).length > 1)}
              id="back"
              onClick={() => {
                if (currentStep === 0 && !!login && (myPractices || []).filter(fff => fff.enabled).length > 1) {
                  setActivePractice(undefined);
                  setCurrentCoach(undefined);
                  setCurrentLocation(undefined);
                  setCurrentType(undefined);
                } else {
                  if (!!searchParams.get("coach_id") && !!searchParams.get("location_id") && currentStep === 3) {
                    setInteracted(true);
                    setCurrentStep(0);
                  } else {
                    setCurrentStep(currentStep - 1);
                  }
                }
                document.getElementById("scroll_anchor")?.scrollIntoView();
              }}
              sx={{
                marginRight: 1
              }}
              label={t("button_back", "Terug")}
            />
            {<>
              <Button
                contained
                disabled={!(
                  currentStep > 4 || (
                    currentStep === 2 && (currentLocation || noLocation)
                  ) || (
                    currentStep === 1 && (!!currentCoach || noCoach)
                  ) || (
                    currentStep === 0 && !!currentType
                  ) || (
                    currentStep === 3 && !!selectedSlot
                  ) || (
                    currentStep === 4 && (!!login || (!login && !!validatedUser) || (!login && !!validUser)) && (!currentType?.note_required || (!!currentType?.note_required && (appointment.note || '').length > 5))
                  )
                ) || !!mutationBookSlot.isPending}
                onClick={() => {
                  console.log(`step: ${currentStep}`);
                  console.log(`coach_id: ${searchParams.get("coach_id")}`);
                  console.log(`currentCoach: ${JSON.stringify(currentCoach)}`);
                  console.log(`interacted: ${interacted}`);
                  if (currentStep === 4) {
                    // console.log(!!login, (!login && !!validatedUser), (!login && !!validUser));
                    mutationBookSlot.mutate({
                      formData: {
                        object: 'appointment',
                        class_action: 'custom_api_book_slot',
                        ...(!!login ? {user_id: login?.id} : {}),
                        ...(!!validatedUser ? {user_id: validatedUser?.id} : {}),
                        ...(!!validUser ? {new_user: newUser} : {}),
                        appointment_type: currentType,
                        slot: selectedSlot,
                        practice_id: activePractice?.id,
                        appointment_attrs: appointment
                      }
                    });
                  } else {
                    if (!interacted && !!searchParams.get("coach_id") && !!searchParams.get("location_id") && currentStep === 0) {
                      setCurrentStep(3);
                    } else {
                      if (!interacted && !!searchParams.get("coach_id") && !!currentCoach && currentStep === 0) {
                        setCurrentStep(2);
                      } else {
                        if (!interacted && !!searchParams.get("location_id") && currentLocation && currentStep === 2) {
                          setCurrentStep(3);
                        } else {
                          setCurrentStep(currentStep + 1);
                        }
                      }
                    }
                    document.getElementById("scroll_anchor")?.scrollIntoView();
                  }
                }}
                sx={{
                  marginRight: 1
                }}
                id="next"
                label={currentStep === 4 ? t("button_complete", "Bevestig") : t("button_next", "Volgende")}
              />
            </>}
          </Box>
        </Box>
      </>}

      <SnackError open={showSnackError} setOpen={setShowSnackError} error={t("appointment.book_slot_fail")} />
      <SnackError open={showSnackBlockedError} setOpen={setShowSnackBlockedError} error={t("appointment.book_slot_fail_blocked")} />
      <Box sx={{display: 'none'}}>{!!showInvalidComboError && <Typography sx={{fontSize: '0.8rem', color: 'red'}}>{t("appointment.invalid_preselect")}</Typography>}</Box>
    </>}
  </Page>;
}

